import stores from './stores';
import products from './products';
import productsTags from './productsTags';
import customers from './customers';
import collaborators from './collaborators';
import catalogManagement from './catalogManagement';
import userRights from './userRights';
import businessProfile from './businessProfile';
import branchDebt from './branchDebt';
import productsPerStore from './productsPerStore';
import productSeries from './productSeries';
import subscriptions from './subscriptions';
import supplierManagement from './supplierManagement';
import giftCategories from './giftCategories';
import businessStatistics from './businessStatistics';
import campaigns from './campaigns';
import redemptions from './redemptions';
import pointTitles from './pointTitles';
import loyaltyCards from './loyaltyCards';
import multipleCoupons from './multipleCoupons';
import notifications from './notifications';
import loyaltyPrograms from './loyaltyPrograms';

import Company from '@/models/loyaltyPanel/Company.js';

const getDefaultState = () => ({
  loading: false,
  company: new Company(),
  companyStores: [],
  companyStoresLoading: false,
  companyPermissions: {},
  company_id: null,
  paymentError: false,
  stripe_pk: '',
  stripe_session_id: '',
  statistics: {},
  homeData: [],
  public_api_token: '',
  tiers_settings: {
    enabled: null,
    package_mode: '',
    tier_mode_id: null,
    system: '',
  },
  storesTotalPages: null,
  loyalties: [],
  loyaltiesPagination: {},
  companyStoresPagination: {},
});

export default {
  namespaced: true,
  modules: {
    stores,
    products,
    productsTags,
    customers,
    collaborators,
    userRights,
    businessProfile,
    catalogManagement,
    branchDebt,
    productSeries,
    productsPerStore,
    subscriptions,
    supplierManagement,
    giftCategories,
    businessStatistics,
    campaigns,
    redemptions,
    pointTitles,
    loyaltyCards,
    multipleCoupons,
    notifications,
    loyaltyPrograms,
  },

  state: () => getDefaultState(),

  getters: {
    store: (state) => state.company,
    companyStores: (state) => state.companyStores,
    companyStoresLoading: (state) => state.companyStoresLoading,
  },

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState());
    },

    setLoading(state, payload) {
      state.loading = payload;
    },

    setCompanyId(state, payload) {
      state.company_id = payload;
    },

    setCompanyPermissions(state, payload) {
      state.companyPermissions = payload;
    },

    setCompany(state, payload) {
      state.company = payload;
    },

    setLogo(state, payload) {
      state.company.logo = payload;
    },

    setLoyaltyLogo(state, payload) {
      state.company.loyalty_logo = payload;
    },

    setMapLogo(state, payload) {
      state.company.map_logo = payload;
    },

    setOfflineMapLogo(state, payload) {
      state.company.offline_map_logo = payload;
    },

    setBusinessInformation(state, payload) {
      state.store.name = payload.name;
      state.store.address = payload.address;
      state.store.category.store_category_id = payload.store_category_id;
      state.store.billing_details.activity = payload.activity;
      state.store.country.country_id = payload.country_id;
      state.store.primary_phone = payload.primary_phone;
      state.store.secondary_phone = payload.secondary_phone;
      state.store.billing_details.mobile = payload.mobile;
      state.store.billing_details.full_name = payload.full_name;
      state.store.email = payload.email;
      state.store.public_api_token = payload.public_api_token;
    },

    setInvoicing(state, payload) {
      state.store.billing_details.comp_name = payload.comp_name;
      state.store.billing_details.vat_number = payload.vat_number;
      state.store.billing_details.city = payload.city;
      state.store.billing_details.occupation = payload.occupation;
      state.store.billing_details.tax_office = payload.tax_office;
      state.store.billing_details.country_id = payload.country_id;
    },

    setQuickPayment(state, payload) {
      state.store.flags.general.quick_payment = payload;
    },

    setOrders(state, payload) {
      state.store.order_settings = payload;
    },

    setRedemption(state, payload) {
      state.store.redemption_type_id = payload;
    },

    setScanReceipt(state, payload) {
      state.store.flags.reward.scan_receipt = payload;
    },

    setRewardWithPresence(state, payload) {
      state.store.flags.reward.open_checkin = payload;
    },

    setMobilePayments(state, payload) {
      state.store.flags.reward.online_payments = payload;
    },

    setCompanyStores(state, payload) {
      state.companyStores = payload;
    },

    setCompanyStoresLoading(state, payload) {
      state.companyStoresLoading = payload;
    },

    setPaymentError(state, payload) {
      state.paymentError = payload;
    },

    setStripePk(state, payload) {
      state.stripe_pk = payload;
    },

    setStripeSessionId(state, payload) {
      state.stripe_session_id = payload;
    },

    setStatistics(state, payload) {
      state.statistics = payload;
    },

    setHomeData(state, payload) {
      state.homeData = payload;
    },

    setPublicApiToken(state, payload) {
      state.public_api_token = payload;
    },

    setTiersSettings(state, payload) {
      state.tiers_settings = payload;
    },
    setStoresTotalPages(state, payload) {
      state.storesTotalPages = payload;
    },
    setLoyalties(state, payload) {
      state.loyalties = payload;
    },
    setLoyaltiesPagination(state, payload) {
      state.loyaltiesPagination = payload;
    },
    setCompanyStoresPagination(state, payload) {
      state.companyStoresPagination = payload;
    },
  },

  actions: {
    async getCompany({ commit, dispatch }) {
      try {
        commit('setLoading', true);
        const { data } = await Company.get();
        commit('setPaymentError', false);
        commit('setCompany', data.data);
        commit('setStore', data.data, { root: true });
        commit('setCompanyPermissions', data.data.permissions);
        commit('setCompanyId', data.data.store_id);
        commit('setPublicApiToken', data.data.public_api_token);
        commit('setLoading', false);
        commit('setTiersSettings', data.data.tier);
      } catch (ex) {
        if (ex.response?.data?.code === 301) {
          commit('setCompany', ex?.response?.data.data);
          commit('setPaymentError', true);
          commit('setStorePermissions', [], { root: true });
          commit('setStripePk', ex?.response?.data.data.pk);
          commit('setStripeSessionId', ex?.response?.data.data.stripe_session_id);
        }
        commit('setLoading', false);
        console.error(ex.response.data.message);
      }
    },

    async getHomePageData({ commit }) {
      try {
        commit('setLoading', true);
        const { data } = await Company.getHomePageData();

        commit('setHomeData', data.data);

        commit('setLoading', false);
      } catch (ex) {
        commit('setLoading', false);
        console.error(ex.response?.data?.message);
      }
    },

    async getStatistics({ commit }) {
      try {
        commit('setLoading', true);
        const { data } = await Company.getStatistics();

        commit('setStatistics', data.data.statistics);

        commit('setLoading', false);
      } catch (ex) {
        commit('setLoading', false);
        console.error(ex.response.data.message);
      }
    },

    async changePaymentStatus({ commit }, body) {
      try {
        await Company.changePaymentStatus(body);
      } catch (ex) {
        commit('setCompanyStoresLoading', false);
      }
    },

    async loadStores({ commit, getters, state }, body) {
      try {
        commit('setCompanyStoresLoading', true);
        if (!body.page) {
          body.page = 1;
        }
        let { data } = await Company.getStores(body.query || '');
        if (body.page === 1) {
          commit('setCompanyStores', data.data.stores);
        } else {
          const stores = state.companyStores;
          data.data.stores.forEach((item) => {
            stores.push(item);
          });
        }

        commit('setServerItemsPerPage', data.data.pagination.items_per_page, {
          root: true,
        });
        commit('setCompanyStoresPagination', data.data.pagination);
        commit('setStoresTotalPages', data.data.pagination.total_pages);
        commit('setCompanyStoresLoading', false);
        return data.data;
      } catch (ex) {
        commit('setCompanyStoresLoading', false);
      }
    },

    async loadStore({ commit, getters, state }, id) {
      try {
        commit('setCompanyStoresLoading', true);
        let { data } = await Company.getStore(id);
        if (data.data) {
          console.log('data.data', data.data);
          state.companyStores.push(data.data.store);
        }
        commit('setCompanyStoresLoading', false);
        return data.data.store;
      } catch (ex) {
        commit('setCompanyStoresLoading', false);
      }
    },

    async loadAllStores({ commit, getters, state }, body) {
      try {
        commit('setCompanyStoresLoading', true);
        let { data } = await Company.getStores(body.query);
        if (body.page === 1) {
          console.log('body.page == 1', data.data);
          commit('setCompanyStores', data.data.stores);
        } else {
          const stores = state.companyStores;

          data.data.stores.forEach((item) => {
            stores.push(item);
          });
        }
        commit('setServerItemsPerPage', data.data.pagination.items_per_page, {
          root: true,
        });
        commit('setStoresTotalPages', data.data.pagination.total_pages);
        commit('setCompanyStoresLoading', false);
      } catch (ex) {
        commit('setCompanyStoresLoading', false);
      }
    },

    async deleteCompany({ commit, getters, state }) {
      try {
        commit('setLoading', true, { root: true });
        let { data } = await Company.deleteCompany();
        commit('setLoading', false, { root: true });
        return data.data;
      } catch (ex) {
        commit(
          'setNotification',
          {
            show: true,
            type: 'error',
            text: ex?.response?.data?.message || 'Error',
          },
          { root: true }
        );

        commit('setLoading', false, { root: true });
      }
    },
    async getLoyalties({ commit, getters, state }, body) {
      try {
        commit('setLoading', true, { root: true });

        const { data } = await Company.getLoyalties(body.query || '');

        if (body.page == 1) {
          commit('setLoyalties', data.data.sub_loyalties);
        } else {
          const loyalties = state.loyalties;
          data.data.sub_loyalties.map((item) => {
            loyalties.push(item);
          });
          commit('setLoyalties', loyalties);
        }

        commit('setLoyaltiesPagination', data.data.pagination);
        commit('setLoading', false, { root: true });
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response?.data || ex);
      }
    },

    async topUp({ state, commit }, body) {
      try {
        commit('setLoading', true, { root: true });

        const { data } = await Company.topUp(body);
        const newCredits = data.data.credits;
        if (newCredits || newCredits === 0) {
          state.company.credits = newCredits;
          commit('setCompany', state.company);
        }
        commit('setLoading', false, { root: true });
        return data;
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response?.data || ex);
        return ex.response?.data || ex;
      }
    },

    async getAutoTopUp({ commit }) {
      try {
        commit('setLoading', true, { root: true });
        const { data } = await Company.getAutoTopUp();
        commit('setLoading', false, { root: true });
        return data;
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response?.data || ex);
        return ex.response?.data || ex;
      }
    },

    async setAutoTopUp({ state, commit }, body) {
      try {
        commit('setLoading', true, { root: true });
        const { data } = await Company.setAutoTopUp(body);
        if (data.credits || data.credits === 0) {
          state.company.credits = data.credits;
          commit('setCompany', state.company);
        }
        commit('setLoading', false, { root: true });
        return data;
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response?.data || ex);
        return ex.response?.data || ex;
      }
    },

    async deleteAutoTopUp({ commit }) {
      try {
        commit('setLoading', true, { root: true });
        const { data } = await Company.deleteAutoTopUp();
        commit('setLoading', false, { root: true });
        return data;
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response?.data || ex);
        return ex.response?.data || ex;
      }
    },
  },
};
